import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import type { Pill, PillScope, Voice, PoliticsMode, CreatorProfile, Client, Rung } from '@/types/database'

/**
 * Topic Mix: the 100% budget, locks, default voices, keyword pills and the
 * account's content gates.
 *
 * The mix is edited locally and saved whole through save_topic_mix(), which
 * checks the total, the cap and the catalogue itself. Moving sliders, adding
 * and removing topics are free; only a reset costs (5 seeds).
 */

export interface MixEntry {
  topic: string
  share: number
  locked: boolean
  voice: Voice
  reach?: Rung | null
}

export function useSaveMix() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (mix: MixEntry[]) => {
      const { error } = await supabase.rpc('save_topic_mix', { p_mix: mix })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['topics', clientId] })
      qc.invalidateQueries({ queryKey: ['pills', clientId] })
    },
  })
}

export function useResetMix() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('reset_topic_mix', {
        p_idem_key: `reset:${clientId}:${Math.floor(Date.now() / 1000)}`,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['topics', clientId] })
      qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

// ---------------- Pills ----------------

export function usePills() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['pills', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('my_pills').select('*').eq('client_id', clientId!)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data as Pill[]
    },
  })
}

export function useAddPill() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { topic: string; term: string; scope?: PillScope }) => {
      const { data, error } = await supabase.rpc('add_pill', {
        p_topic: args.topic, p_term: args.term, p_scope: args.scope ?? null,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pills', clientId] }),
  })
}

export function useRemovePill() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('keyword_pills').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pills', clientId] }),
  })
}

/** Researches pills that have no research yet, or have gone stale. 1 seed
 *  each, charged only for the ones Emma actually answered. */
export function useResearchPills() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (pillIds: string[]) => {
      const { data, error } = await supabase.functions.invoke('pill-research', {
        body: { pill_ids: pillIds },
      })
      if (error) throw await functionError(error)
      return data as { results: unknown[]; failed?: number; charged: number }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pills', clientId] })
      qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

export function useDismissSeason() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { userId: string; topic: string; termNorm: string }) => {
      const { error } = await supabase.from('pill_season_dismissals').insert({
        user_id: args.userId, topic: args.topic, term_norm: args.termNorm,
        season_year: new Date().getFullYear(),
      })
      if (error && !/duplicate key/i.test(error.message)) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pills', clientId] }),
  })
}

/** Points a closing seasonal pill at what usually comes next. Keeps the slot
 *  and costs nothing; the renamed pill is researched again like any new one. */
export function useRenamePill() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: string; term: string }) => {
      const { error } = await supabase.rpc('rename_pill', { p_pill_id: args.id, p_term: args.term })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pills', clientId] }),
  })
}

// ---------------- Account content settings ----------------

/** How places are keyed for density and capability records: "Salem, OR". */
export function placeKey(name: string, state?: string | null) {
  return (state ? `${name}, ${state}` : name).trim().toLowerCase()
}

export type ClientPrefs = Pick<Client, 'id' | 'display_name' | 'city_state' | 'state'> & {
  family_friendly: boolean
  politics_mode: PoliticsMode
  creator_profile: CreatorProfile
  newsletter_enabled: boolean
  plan_id: string | null
  created_at: string
}

export function useClientPrefs() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['client-prefs', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, display_name, city_state, state, family_friendly, politics_mode, creator_profile, newsletter_enabled, plan_id, created_at')
        .eq('id', clientId!).single()
      if (error) throw error
      return data as ClientPrefs
    },
  })
}

export function useUpdateClientPrefs() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<Pick<ClientPrefs, 'family_friendly' | 'politics_mode' | 'creator_profile'>>) => {
      const { error } = await supabase.from('clients').update(patch).eq('id', clientId!)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client-prefs', clientId] }),
  })
}

/** What each of the account's places can supply per local topic. */
export function usePlaceDisclosure(places: string[]) {
  return useQuery({
    queryKey: ['disclosure', places.join('|')],
    enabled: places.length > 0,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('place_disclosure', { p_places: places })
      if (error) throw error
      return data as { place_key: string; topic: string; availability: 'records' | 'partial' | 'news' }[]
    },
  })
}

/** Density for each topic: the account's weakest place is the honest answer,
 *  so this returns that band and which place it came from. Null until enough
 *  pulls exist to say. */
export function useDensity(placeKeys: string[], topics: string[]) {
  return useQuery({
    queryKey: ['density', placeKeys.join('|'), topics.join('|')],
    enabled: placeKeys.length > 0 && topics.length > 0,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const order = ['thin', 'moderate', 'strong']
      const out: Record<string, { band: 'thin' | 'moderate' | 'strong'; place: string } | null> = {}
      await Promise.all(topics.map(async (t) => {
        const bands = await Promise.all(placeKeys.map(async (p) => {
          const { data } = await supabase.rpc('density_band', { p_place_key: p, p_topic: t })
          return { place: p, band: (data as string | null) ?? null }
        }))
        const known = bands.filter((b) => b.band) as { place: string; band: 'thin' | 'moderate' | 'strong' }[]
        out[t] = known.length ? known.sort((a, b) => order.indexOf(a.band) - order.indexOf(b.band))[0] : null
      }))
      return out
    },
  })
}

/**
 * Edge functions return their message in the JSON body; supabase-js wraps a
 * non-2xx in a FunctionsHttpError whose own message is generic. Pull the real
 * one out so the UI can say what actually happened.
 */
export async function functionError(error: unknown): Promise<Error & { status?: number }> {
  const e = error as { message?: string; context?: Response }
  let message = e?.message ?? 'Something went wrong.'
  let status: number | undefined
  try {
    if (e?.context && typeof e.context.json === 'function') {
      status = e.context.status
      const body = await e.context.clone().json()
      if (body?.error) message = body.error
    }
  } catch { /* keep the generic message */ }
  const out = new Error(message) as Error & { status?: number }
  out.status = status
  return out
}
